import React from "react";
import "./index.css";
import { Link } from "react-router-dom";

import Icon from "../../../../../../components/Icon";

/* Components */
import TextRow from "../TextRow";

/* Helpers */
import { handleAvailability, constructObjective, phoneOrWhatsapp, balancePersonalDetails } from "../../../../../../helpers/candidate";
import { handleTimePeriod, latestFirst, readDob, isRealDate } from "../../../../../../helpers/date";
import { handlePartner } from "../../../../../../helpers/couple";
import { cleanProfileText } from "../../../../../../helpers/misc";

const SectionSeparator = ({ title }) => (
    <div className="cv-section-separator">
        <span className="cv-section-separator-bar" />
        <span className="cv-section-separator-title">{(title || "").toUpperCase()}</span>
        <span className="cv-section-separator-rule" />
    </div>
);

const Bullet = ({ text, expiry }) => (
    <div className="cv-bullet">
        <span className="cv-bullet-dot">•</span>
        <span className="cv-bullet-text">
            {text}
            {isRealDate(expiry) ? <span className="cv-bullet-date"> (exp. {expiry})</span> : null}
        </span>
    </div>
);

export default ({ user, admin, application, verifyReference }) => {

    if (!user) return null;

    let references = admin ? user.profile.references : user.profile.references.filter(item => !item.archived);

        const partner = user?.profile?.couple ? handlePartner(user.profile.couple, user._id) : null;
        const personalDetails = balancePersonalDetails(user.profile);

        return(
            <div className="cv-container cv-container--v2">
                <div className="cv-meta-container">
                    <div className="cv-meta-content-contianer">
                        {!application &&
                            <div className="cv-name-container">
                                <p>{user.profile.firstName} {user.profile.lastName}</p>
                                {user?.profile?.lookingFor?.desiredRoles[0]?.position[0]?.name &&
                                    <span>{user?.profile?.lookingFor?.desiredRoles[0]?.position[0]?.name.toUpperCase()}</span>
                                }
                            </div>
                        }
                        <div className="cv-meta-basic-information-container">
                            {user?.profile?.currentLocation &&
                                <TextRow propName={"Location"} value={user.profile.currentLocation.name} />
                            }
                            <TextRow propName={"Availability"} value={handleAvailability(user.profile)} />
                            <TextRow propName={"Email"} value={user.profile.email} type={"email"} />
                            {user?.profile?.primaryPhone &&
                                <TextRow propName={"Mobile Phone"} admin={admin} type={"mobile"} phoneNumber={user.profile.primaryPhone} value={phoneOrWhatsapp(admin, user.profile.primaryPhone)} whatsapp={user.profile.primaryPhoneWhatsApp} />
                            }
                            {user?.profile?.secondaryPhone &&
                                <TextRow propName={"Other Phone"} admin={admin} type={"mobile"} phoneNumber={user.profile.secondaryPhone} value={phoneOrWhatsapp(admin, user.profile.secondaryPhone)} whatsapp={user.profile.secondaryPhoneWhatsApp} />
                            }
                            {user?.profile?.dob &&
                                <TextRow propName={"Date of Birth"} value={readDob(user.profile.dob)} />
                            }
                        </div>
                    </div>
                    {user?.profile?.profileImage &&
                        <div className="cv-profile-image-container">
                            <img src={user.profile.profileImage} alt="" />
                        </div>
                    }
                </div>
                {(personalDetails[0].length + personalDetails[1].length) > 0 &&
                    <>
                        <SectionSeparator title="Personal Details" />
                        <div className="cv-section-container">
                            <div className="cv-section-list-container">
                                {personalDetails[0].map((detail, index) => (
                                    <div key={index}>{detail}</div>
                                ))}
                            </div>
                            <div className="cv-section-list-container">
                                {personalDetails[1].map((detail, index) => (
                                    <div key={index}>{detail}</div>
                                ))}
                            </div>
                        </div>
                    </>
                }
                {user?.profile?.aboutMe &&
                    <>
                        <SectionSeparator title="Profile" />
                        <div className="cv-section-container">
                            <p className="cv-section-text">{cleanProfileText(user.profile.aboutMe)}</p>
                        </div>
                    </>
                }
                {user?.profile?.lookingFor?.desiredRoles[0]?.position[0]?.name &&
                    <>
                        <SectionSeparator title="Objective" />
                        <div className="cv-section-container" style={{ flexDirection: "column" }}>
                            {partner?.desiredRoles?.length > 0 &&
                                <p className="cv-section-text" style={{ paddingBottom: 10 }}>{`I am looking for a position with my partner${partner.referenceName ? ` ${partner.referenceName}` : ""}.`}</p>
                            }
                            {user.profile.lookingFor.desiredRoles.map((role, index) => (
                                <p className="cv-section-text" key={index}>{constructObjective(role, index, user.profile.lookingFor.desiredRoles)}</p>
                            ))}
                        </div>
                    </>
                }
                {(user?.profile?.qualifications.length !== 0 || user?.profile?.eng1Info?.status || user?.profile?.stcwBasicInfo?.status || user?.profile?.stcwAdvancedInfo?.status) &&
                    <>
                        <SectionSeparator title="License & Qualifications" />
                        <div className="cv-section-container">
                            <div className="cv-section-list-container">
                                {user?.profile?.eng1Info?.status &&
                                    <Bullet text="ENG 1" expiry={user.profile.eng1Info.expiryDate} />
                                }
                                {user.profile.qualifications.slice(0, ((parseInt(user.profile.qualifications.length / 2))+1)).map((quality, index) => (
                                    <Bullet key={index} text={quality.name} />
                                ))}
                            </div>
                            <div className="cv-section-list-container">
                                {user?.profile?.stcwBasicInfo?.status &&
                                    <Bullet text="Basic STCW Safety Training" expiry={user.profile.stcwBasicInfo.expiryDate} />
                                }
                                {user?.profile?.stcwAdvancedInfo?.status &&
                                    <Bullet text="Advanced STCW Safety Training" expiry={user.profile.stcwAdvancedInfo.expiryDate} />
                                }
                                {user.profile.qualifications.slice((parseInt(user.profile.qualifications.length / 2))+1).map((quality, index) => (
                                    <Bullet key={index} text={quality.name} />
                                ))}
                            </div>
                        </div>
                    </>
                }
                {user?.profile?.pastRoles.length !== 0 &&
                    <>
                        <SectionSeparator title="Experience" />
                        <div className="cv-timeline">
                            {user.profile.pastRoles.sort(latestFirst).map((role, index) => (
                                <div className="cv-timeline-entry" key={index}>
                                    <div className="cv-timeline-header">
                                        <div className="cv-timeline-main">
                                            <p className="cv-timeline-role">{role.position}</p>
                                            {role.companyName && <p className="cv-timeline-company">{role.companyName}</p>}
                                        </div>
                                        <p className="cv-timeline-date">{handleTimePeriod(role.fromDate, role.toDate, role.present)}</p>
                                    </div>
                                    {role.description && <p className="cv-timeline-description">{cleanProfileText(role.description)}</p>}
                                </div>
                            ))}
                        </div>
                    </>
                }
                {user?.profile?.education.length !== 0 &&
                    <>
                        <SectionSeparator title="Education" />
                        <div className="cv-timeline">
                            {user.profile.education.sort(latestFirst).map((edu, index) => (
                                <div className="cv-timeline-entry" key={index}>
                                    <div className="cv-timeline-header">
                                        <div className="cv-timeline-main">
                                            <p className="cv-timeline-role">{edu.name}</p>
                                        </div>
                                        <p className="cv-timeline-date">{handleTimePeriod(edu.fromDate, edu.toDate, edu.present, true)}</p>
                                    </div>
                                    {edu.description && <p className="cv-timeline-description">{cleanProfileText(edu.description)}</p>}
                                </div>
                            ))}
                        </div>
                    </>
                }
                {user?.profile?.skills.length !== 0 &&
                    <>
                        <SectionSeparator title="Skills" />
                        <div className="cv-section-container">
                            <div className="cv-section-list-container">
                                {user.profile.skills.slice(0, ((parseInt(user.profile.skills.length / 2))+1)).map((skill, index) => (
                                    <Bullet key={index} text={skill} />
                                ))}
                            </div>
                            <div className="cv-section-list-container">
                                {user.profile.skills.slice((parseInt(user.profile.skills.length / 2))+1).map((skill, index) => (
                                    <Bullet key={index} text={skill} />
                                ))}
                            </div>
                        </div>
                    </>
                }
                {user?.profile?.equipment.length !== 0 &&
                    <>
                        <SectionSeparator title="Equipment Knowledge" />
                        <div className="cv-section-container">
                            <div className="cv-section-list-container">
                                {user.profile.equipment.slice(0, ((parseInt(user.profile.equipment.length / 2))+1)).map((item, index) => (
                                    <Bullet key={index} text={item} />
                                ))}
                            </div>
                            <div className="cv-section-list-container">
                                {user.profile.equipment.slice((parseInt(user.profile.equipment.length / 2))+1).map((item, index) => (
                                    <Bullet key={index} text={item} />
                                ))}
                            </div>
                        </div>
                    </>
                }
                {user?.profile?.hobbies &&
                    <>
                        <SectionSeparator title="Interests & Hobbies" />
                        <div className="cv-section-container">
                            <p className="cv-section-text">{cleanProfileText(user.profile.hobbies)}</p>
                        </div>
                    </>
                }
                {references.length !== 0 &&
                    <>
                        <SectionSeparator title="References" />
                        <div className="cv-references">
                            {references.map((reference, index) => {
                                const personParts = [reference.referenceName, reference.position].filter(Boolean);
                                return (
                                    <div className="cv-reference-item" key={index}>
                                        <div className="cv-reference-header">
                                            <p className="cv-reference-company">{reference.companyName}</p>
                                            {admin && reference.completed &&
                                                <span className="cv-reference-badge" aria-label="Verified"><Icon name="badge-check" size={16} /></span>
                                            }
                                            {admin && reference.emailSent && !reference.completed &&
                                                <span className="cv-reference-status">Email Sent</span>
                                            }
                                            {admin && reference.archived &&
                                                <span className="cv-reference-status cv-reference-status--archived">Archived</span>
                                            }
                                            {admin && !reference.completed && !reference.emailSent &&
                                                <span className="cv-reference-action" onClick={() => verifyReference(user.profile, reference)}>Verify Reference</span>
                                            }
                                        </div>
                                        {personParts.length > 0 &&
                                            <p className="cv-reference-person">{personParts.join(" · ")}</p>
                                        }
                                        {(reference.email || reference.contactNumber) &&
                                            <p className="cv-reference-contact">
                                                {reference.email &&
                                                    <a href={`mailto:${reference.email}`}>{reference.email}</a>
                                                }
                                                {reference.email && reference.contactNumber &&
                                                    <span className="cv-reference-contact-sep">·</span>
                                                }
                                                {reference.contactNumber && <span>{reference.contactNumber}</span>}
                                            </p>
                                        }
                                        {admin && reference.completed &&
                                            <div className="cv-reference-document">
                                                <TextRow propName={"Document"} type={"file"} value={reference} candidate={user.profile} />
                                            </div>
                                        }
                                    </div>
                                );
                            })}
                        </div>
                    </>
                }
            </div>
        );

}
